import React, { Fragment, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Combobox, Switch, Transition } from '@headlessui/react'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import { randomToastSuccess } from '@/shared/Toast'
import { CheckIcon, ExclamationCircleIcon, ExternalLinkIcon, SelectorIcon, XIcon } from '@heroicons/react/solid'
import { Link } from 'react-router-dom'

import Card from '@/shared/Card'
import Breadcrumbs from '@/Layout/Breadcrumbs'
import PageTitle from '@/Layout/PageTitle'
import PrimaryButton from '@/shared/Buttons/Primary'
import { Modal, useModal } from '@/shared/Modal'
import ImageUploadFormR2 from '@/shared/ImageUploadFormR2'

// Splits the stored string into tag strings. Matches the same split the public
// profile uses so what you see here === what renders on /profiles/:username.
const parseInterestTags = (raw) =>
  (raw || '').split(/[,\n]/).map(s => s.trim()).filter(Boolean)

const InterestsInput = ({ value, onChange, error, maxLength = 500 }) => {
  const tags = parseInterestTags(value)
  const [draft, setDraft] = useState('')

  const writeTags = (next) => {
    // Dedupe case-insensitively but preserve first-seen casing.
    const seen = new Set()
    const deduped = next.filter(t => {
      const key = t.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    let joined = deduped.join(', ')
    if (joined.length > maxLength) joined = joined.slice(0, maxLength)
    onChange(joined)
  }

  const commit = (raw) => {
    const pieces = raw.split(',').map(s => s.trim()).filter(Boolean)
    if (pieces.length === 0) return
    writeTags([...tags, ...pieces])
    setDraft('')
  }

  const removeAt = (idx) => writeTags(tags.filter((_, i) => i !== idx))

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      commit(draft)
    } else if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
      e.preventDefault()
      writeTags(tags.slice(0, -1))
    }
  }

  return (
    <div
      className={`flex flex-wrap items-center gap-2 px-3 py-2 rounded-md border bg-white dark:bg-slate-800 focus-within:ring-1 focus-within:ring-clcblue focus-within:border-clcblue min-h-[2.75rem] ${error ? 'border-red-500' : 'border-slate-300 dark:border-slate-600'}`}
      onClick={(e) => { if (e.target === e.currentTarget) e.currentTarget.querySelector('input')?.focus() }}
    >
      {tags.map((tag, i) => (
        <span key={`${tag}-${i}`} className='inline-flex items-center gap-1 rounded-full bg-clcpurple/10 text-clcnavy-dark px-3 py-1 text-sm font-medium'>
          {tag}
          <button
            type='button'
            onClick={() => removeAt(i)}
            className='inline-flex items-center justify-center h-4 w-4 rounded-full text-clcnavy-dark/50 hover:text-clcnavy-dark hover:bg-clcpurple/20 focus:outline-none'
            aria-label={`Remove ${tag}`}
          >
            <XIcon className='h-3 w-3' />
          </button>
        </span>
      ))}
      <input
        type='text'
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
        placeholder={tags.length === 0 ? 'Animation, audiobooks, sci-fi, video games...' : 'Add another'}
        className='flex-1 min-w-[8rem] border-0 p-0 bg-transparent text-sm focus:ring-0 focus:outline-none'
        style={{ boxShadow: 'none' }}
      />
    </div>
  )
}

const formatTzLabel = (tz) => {
  if (!tz) return ''
  const lastSlash = tz.computer.lastIndexOf('/')
  const city = lastSlash >= 0 ? tz.computer.slice(lastSlash + 1).replace(/_/g, ' ') : tz.computer
  const offset = (tz.human.match(/\(UTC[^)]*\)/) || [''])[0]
  return `${city}${offset ? ` ${offset}` : ''}`
}

const ProfileEdit = () => {
  const [currentUser, setCurrentUser] = useGlobalState('currentUser')
  const [, setToast] = useGlobalState('toast')
  const { putpostRequest, getRequest } = useQuery()
  const [timezones, setTimezones] = useState([])
  const [loading, setLoading] = useState(false)

  const [tzQuery, setTzQuery] = useState('')
  const { openModal, closeModal, isOpen } = useModal()

  const { formState, register, handleSubmit, setValue, getValues, setError, watch, control, reset } = useForm({
    defaultValues: {
      first_name: currentUser?.firstName || '',
      last_name: currentUser?.lastName || '',
      username: currentUser?.username || '',
      timezone: currentUser?.timezone || '',
      image_url: currentUser?.imageUrl || '',
      description: currentUser?.description || '',
      interests: currentUser?.interests || '',
      show_courses_on_profile: currentUser?.showCoursesOnProfile ?? true
    }
  })

  const { isDirty, errors } = formState
  const watchedImageUrl = watch('image_url')
  const watchedDescription = watch('description') || ''
  const watchedInterests = watch('interests') || ''

  useEffect(() => {
    getRequest('/api/v1/static/timezones', {}, (err, jsonData) => {
      if (err) return
      if (jsonData.timezones) {
        setTimezones(jsonData.timezones)
      }
    })
  }, [])

  // Fetch fresh user data from the server on mount so the form reflects what's actually
  // in the DB, not whatever is in the in-memory currentUser global state.
  useEffect(() => {
    if (!currentUser?.id) return
    getRequest(`/api/v1/users/${currentUser.id}/edit`, {}, (err, jsonData) => {
      if (err || !jsonData?.user) return
      const u = jsonData.user
      reset({
        first_name: u.firstName || '',
        last_name: u.lastName || '',
        username: u.username || '',
        timezone: u.timezone || '',
        image_url: u.imageUrl || '',
        description: u.description || '',
        interests: u.interests || '',
        show_courses_on_profile: u.showCoursesOnProfile ?? true
      })
      // Also push the fresh values into global state so the rest of the app reflects them.
      setCurrentUser({
        ...currentUser,
        firstName: u.firstName,
        lastName: u.lastName,
        displayName: u.displayName,
        timezone: u.timezone,
        username: u.username,
        imageUrl: u.imageUrl,
        description: u.description,
        interests: u.interests,
        showCoursesOnProfile: u.showCoursesOnProfile
      })
    })
  }, [currentUser?.id])

  const onSubmit = (data) => {
    // Strip undefined values so they don't get JSON.stringify'd away silently
    const payload = Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k, v === undefined ? null : v])
    )
    setLoading(true)
    putpostRequest(`/api/v1/users/${currentUser.id}`, 'PATCH', { user: payload }, (err, jsonData) => {
      setLoading(false)
      if (err) {
        Object.entries(err).forEach(([key, value]) => setError(key, { type: 'server', message: Array.isArray(value) ? value.join(', ') : value }))
        return
      }

      const u = jsonData.user
      setCurrentUser({
        ...currentUser,
        firstName: u.firstName,
        lastName: u.lastName,
        displayName: u.displayName,
        timezone: u.timezone,
        username: u.username,
        imageUrl: u.imageUrl,
        description: u.description,
        interests: u.interests,
        showCoursesOnProfile: u.showCoursesOnProfile
      })

      // Sync the form's internal state (incl. isDirty) to what the server actually saved.
      // Snake_case keys here match the form's register() names.
      reset({
        first_name: u.firstName || '',
        last_name: u.lastName || '',
        username: u.username || '',
        timezone: u.timezone || '',
        image_url: u.imageUrl || '',
        description: u.description || '',
        interests: u.interests || '',
        show_courses_on_profile: u.showCoursesOnProfile ?? true
      })

      setToast(randomToastSuccess())
    })
  }

  if (!currentUser?.id) return null

  return (
    <>
    <Breadcrumbs>
      <span className='text-sm font-medium text-slate-700 dark:text-slate-200'>Profile</span>
    </Breadcrumbs>
    <PageTitle pagetitle='Your profile' />
    {/* Width and horizontal padding come from the layout container, same as
        every other page; this used to pin itself to max-w-4xl and re-apply
        the container's padding on top. */}
    <div className='py-8'>
      <div className='mb-8'>
        <p className='mt-2 text-base text-slate-600 dark:text-slate-400'>
          This is what others see on your public profile page.
          {currentUser?.username && (
            <Link to={`/profiles/${currentUser.username}`} className='ml-2 inline-flex items-center font-medium text-clcblue dark:text-clcnavy-light hover:text-clcnavy-dark'>
              View public profile <ExternalLinkIcon className='h-4 w-4 ml-1' />
            </Link>
          )}
        </p>
      </div>

      <Card footer={<PrimaryButton onClick={handleSubmit(onSubmit)} loading={loading} disabled={!isDirty} text='Save' />}>
        <form className='space-y-6'>

          {/* Profile picture */}
          <div>
            <label className='block text-sm font-medium text-slate-700 dark:text-slate-300'>Profile picture</label>
            <div className='mt-2 flex items-center gap-4'>
              <div className='h-20 w-20 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0'>
                {watchedImageUrl ? (
                  <img src={watchedImageUrl} alt='' className='h-full w-full object-cover' onError={(e) => { e.target.style.display = 'none' }} />
                ) : (
                  <span className='text-2xl text-slate-400 capitalize'>{currentUser?.firstName?.[0] || currentUser?.email?.[0] || '?'}</span>
                )}
              </div>
              <div className='flex-1'>
                <button type='button' onClick={openModal} className='inline-flex items-center px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-clcblue'>
                  {watchedImageUrl ? 'Change image' : 'Upload image'}
                </button>
                <p className='mt-1 text-xs text-slate-500'>Don't forget to click Save below.</p>
              </div>
            </div>
            <input type='hidden' {...register('image_url')} />
          </div>

          <Modal isOpen={isOpen} closeModal={closeModal}>
            <h3 className='text-lg leading-6 font-medium text-gray-900 dark:text-white'>Profile picture</h3>
            <div className='mt-4'>
              <ImageUploadFormR2
                kind='User'
                aspect='square'
                url={watchedImageUrl}
                setUrl={(u) => setValue('image_url', u, { shouldDirty: true })}
                onDone={closeModal}
              />
            </div>
          </Modal>

          {/* First / Last name */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label htmlFor='first_name' className='block text-sm font-medium text-slate-700 dark:text-slate-300'>First name</label>
              <div className='mt-1 relative'>
                <input type='text' className={errors.first_name ? 'errors' : ''} placeholder='Happy' {...register('first_name', { required: true })} />
                {errors.first_name && <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
                  <ExclamationCircleIcon className='h-5 w-5 text-red-500' />
                </div>}
              </div>
              {errors.first_name && <p className='mt-1 text-xs text-red-600'>Required</p>}
            </div>
            <div>
              <label htmlFor='last_name' className='block text-sm font-medium text-slate-700 dark:text-slate-300'>Last name</label>
              <div className='mt-1 relative'>
                <input type='text' className={errors.last_name ? 'errors' : ''} placeholder='Hedgehog' {...register('last_name', { required: true })} />
                {errors.last_name && <div className='absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none'>
                  <ExclamationCircleIcon className='h-5 w-5 text-red-500' />
                </div>}
              </div>
              {errors.last_name && <p className='mt-1 text-xs text-red-600'>Required</p>}
            </div>
          </div>

          {/* Username */}
          <div>
            <label htmlFor='username' className='block text-sm font-medium text-slate-700 dark:text-slate-300'>Username</label>
            <div className='mt-1 flex'>
              <span className='inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-slate-300 bg-slate-50 text-slate-500 text-sm shrink-0'>
                /profiles/
              </span>
              <input type='text' placeholder='happy-hedgehog' className={`flex-1 min-w-0 !rounded-l-none ${errors.username ? 'errors' : ''}`} {...register('username', {
                required: true,
                pattern: { value: /^[a-z0-9_-]{3,30}$/i, message: '3-30 chars, lowercase letters, numbers, dashes, underscores' }
              })} />
            </div>
            {errors.username && <p className='mt-1 text-xs text-red-600'>{errors.username.message || 'Invalid username'}</p>}
            <p className='mt-1 text-xs text-slate-500'>Used in your public profile URL.</p>
          </div>

          {/* Timezone, searchable combobox */}
          <div>
            <label className='block text-sm font-medium text-slate-700 dark:text-slate-300'>Time zone</label>
            <Controller
              name='timezone'
              control={control}
              render={({ field }) => {
                const filtered = tzQuery === ''
                  ? timezones
                  : timezones.filter(tz =>
                      formatTzLabel(tz).toLowerCase().includes(tzQuery.toLowerCase()) ||
                      tz.computer.toLowerCase().includes(tzQuery.toLowerCase())
                    )
                return (
                  <Combobox value={field.value || ''} onChange={field.onChange}>
                    <div className='relative mt-1'>
                      <div className='relative w-full'>
                        <Combobox.Input
                          className='w-full pr-10 truncate'
                          displayValue={(tzComputer) => formatTzLabel(timezones.find(t => t.computer === tzComputer))}
                          onChange={(e) => setTzQuery(e.target.value)}
                          placeholder='Search time zones...'
                        />
                        <Combobox.Button className='absolute inset-y-0 right-0 flex items-center pr-2'>
                          <SelectorIcon className='h-5 w-5 text-slate-400' aria-hidden='true' />
                        </Combobox.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        leave='transition ease-in duration-100'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'
                        afterLeave={() => setTzQuery('')}
                      >
                        <Combobox.Options className='absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-slate-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
                          {filtered.length === 0 && tzQuery !== '' ? (
                            <div className='relative cursor-default select-none py-2 px-4 text-slate-500'>
                              No matches.
                            </div>
                          ) : (
                            filtered.map(tz => (
                              <Combobox.Option
                                key={tz.computer}
                                value={tz.computer}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-clcblue text-white' : 'text-slate-900 dark:text-slate-100'}`
                                }
                              >
                                {({ selected, active }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                      {formatTzLabel(tz)}
                                    </span>
                                    {selected && (
                                      <span className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-clcblue dark:text-clcnavy-light'}`}>
                                        <CheckIcon className='h-5 w-5' aria-hidden='true' />
                                      </span>
                                    )}
                                  </>
                                )}
                              </Combobox.Option>
                            ))
                          )}
                        </Combobox.Options>
                      </Transition>
                    </div>
                  </Combobox>
                )
              }}
            />
          </div>

          {/* Description */}
          <div>
            <div className='flex justify-between items-baseline'>
              <label htmlFor='description' className='block text-sm font-medium text-slate-700 dark:text-slate-300'>About</label>
              <span className='text-xs text-slate-500'>{watchedDescription.length}/1000</span>
            </div>
            <div className='mt-1'>
              <textarea rows={5} placeholder='A short bio. Where you are in your craft, what you love about it, what you want to do next.' className={errors.description ? 'errors' : ''} {...register('description', { maxLength: 1000 })} />
            </div>
            {errors.description && <p className='mt-1 text-xs text-red-600'>Max 1000 characters</p>}
          </div>

          {/* Interests */}
          <div>
            <div className='flex justify-between items-baseline'>
              <label htmlFor='interests' className='block text-sm font-medium text-slate-700 dark:text-slate-300'>Interests</label>
              <span className='text-xs text-slate-500'>{parseInterestTags(watchedInterests).length} {parseInterestTags(watchedInterests).length === 1 ? 'tag' : 'tags'}</span>
            </div>
            <div className='mt-1'>
              <Controller
                name='interests'
                control={control}
                rules={{ maxLength: 500 }}
                render={({ field }) => (
                  <InterestsInput value={field.value} onChange={field.onChange} error={!!errors.interests} />
                )}
              />
            </div>
            <p className='mt-1 text-xs text-slate-500'>Press <kbd className='px-1 py-0.5 text-[0.65rem] rounded border border-slate-300 bg-slate-50'>Enter</kbd> or <kbd className='px-1 py-0.5 text-[0.65rem] rounded border border-slate-300 bg-slate-50'>,</kbd> to add a tag. Click <span className='inline-block align-middle'>&times;</span> to remove.</p>
            {errors.interests && <p className='mt-1 text-xs text-red-600'>Max 500 characters total</p>}
          </div>

          {/* Privacy: show courses toggle */}
          <div className='pt-4 border-t border-slate-100 dark:border-slate-800'>
            <Controller
              name='show_courses_on_profile'
              control={control}
              render={({ field }) => (
                <Switch.Group as='div' className='flex items-start justify-between gap-4'>
                  <span className='flex flex-col flex-1'>
                    <Switch.Label as='span' className='text-sm font-medium text-slate-700 dark:text-slate-300' passive>
                      Show the courses I've taken on my profile
                    </Switch.Label>
                    <Switch.Description as='span' className='text-xs text-slate-500 dark:text-slate-400 mt-1'>
                      When off, your enrollment history is hidden from your public profile.
                    </Switch.Description>
                  </span>
                  <Switch
                    checked={!!field.value}
                    onChange={field.onChange}
                    className={`${field.value ? 'bg-clcblue' : 'bg-slate-300 dark:bg-slate-600'} relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-clcblue focus:ring-offset-2`}
                  >
                    <span
                      aria-hidden='true'
                      className={`${field.value ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                    />
                  </Switch>
                </Switch.Group>
              )}
            />
          </div>

        </form>
      </Card>
    </div>
    </>
  )
}

export default ProfileEdit
